import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { AppNode, MultiAngleParams, NodeStatus } from '@/types';
import { useModelStore } from '@/stores/modelStore';
import { getModelCost } from '@/utils/credits';
import { InverseScaleBox } from './InverseScaleBox';
import { Slider } from '@/components/director/ui/Slider';
import { RotateCcw } from 'lucide-react';
import { MultiAngleViewport } from './multi-angle/MultiAngleViewport';
import {
  ANGLE_PRESETS,
  AZIMUTH_BUTTONS,
  ELEVATION_BUTTONS,
  SCALE_BUTTONS,
  SCALE_LABELS,
  DEFAULT_MULTI_ANGLE_PARAMS,
  clampAzimuth,
  clampElevation,
  clampScaleIndex,
  snapScaleIndex,
  matchPresetId,
  AnglePreset,
} from './multi-angle/anglePresets';
import { buildAnglePrompt } from './multi-angle/buildAnglePrompt';

const ACCENT = '#6D5BF5';
// 多角度生成固定使用 WaveSpeed 96 机位多角度相机模型（用户无需选择模型）
const GENERATE_MODEL_ID = 'wavespeed-qwen-image-edit-multiple-angles';

interface MultiAnglePanelProps {
  data: AppNode;
  cardHeight: number;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onGenerateMultiAngle?: (sourceNodeId: string, params: {
    prompt: string;
    imageUrl: string;
    model?: string;
    aspectRatio?: string;
    multiAngleParams: MultiAngleParams;
  }) => void;
  onClose: () => void;
}

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2L10 10M10 2L2 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PresetTab = memo<{ label: string; active: boolean; onClick: () => void }>(({ label, active, onClick }) => (
  <button
    onMouseDown={(e) => e.stopPropagation()}
    onClick={onClick}
    className={`h-7 px-2.5 rounded-md text-[11px] whitespace-nowrap border transition-colors cursor-pointer ${
      active
        ? 'bg-[#2c2c2e] text-white border-white/20'
        : 'bg-white/5 text-white/50 border-transparent hover:text-white/80 hover:bg-[#3a3a3d]'
    }`}
  >
    {label}
  </button>
));
PresetTab.displayName = 'PresetTab';

const MiniButton = memo<{ label: string; active: boolean; onClick: () => void }>(({ label, active, onClick }) => (
  <button
    onMouseDown={(e) => e.stopPropagation()}
    onClick={onClick}
    className={`h-7 rounded-md text-[11px] transition-colors cursor-pointer ${
      active ? 'text-white' : 'bg-[#2c2c2e] text-white/60 hover:bg-[#3a3a3d] hover:text-white/90'
    }`}
    style={active ? { background: ACCENT } : undefined}
  >
    {label}
  </button>
));
MiniButton.displayName = 'MiniButton';

const SectionLabel = memo<{ text: string; valueText: string }>(({ text, valueText }) => (
  <div className="flex items-center justify-between">
    <span className="text-[11px] text-white/80">{text}</span>
    <span className="text-[11px] text-white/50 font-mono tabular-nums">{valueText}</span>
  </div>
));
SectionLabel.displayName = 'SectionLabel';

const ToggleSwitch = memo<{ checked: boolean; onChange: (v: boolean) => void }>(({ checked, onChange }) => (
  <button
    onMouseDown={(e) => e.stopPropagation()}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={`relative w-[30px] h-[17px] rounded-full transition-colors cursor-pointer ${checked ? '' : 'bg-white/15'}`}
    style={checked ? { background: ACCENT } : undefined}
  >
    <span
      className="absolute top-[2px] left-[2px] w-[13px] h-[13px] bg-white rounded-full transition-transform"
      style={{ transform: checked ? 'translateX(13px)' : 'translateX(0)' }}
    />
  </button>
));
ToggleSwitch.displayName = 'ToggleSwitch';

export const MultiAnglePanel: React.FC<MultiAnglePanelProps> = ({
  data,
  cardHeight,
  onNodeUpdate,
  onGenerateMultiAngle,
  onClose,
}) => {
  const imageUrl = data.data.image;
  const isWorking = data.status === NodeStatus.WORKING;
  // 积分系统 P1：固定模型定价预估（未配置 = 0）
  const models = useModelStore((s) => s.models);

  const [params, setParams] = useState<MultiAngleParams>(() => {
    const saved = data.data.multiAngleParams;
    const azimuth = clampAzimuth(saved?.azimuth ?? 0);
    const elevation = clampElevation(saved?.elevation ?? 0);
    const scaleIndex = snapScaleIndex(saved?.scaleIndex ?? 2);
    return {
      presetId: saved?.presetId ?? matchPresetId({ azimuth, elevation, scaleIndex }),
      azimuth,
      elevation,
      scaleIndex,
      promptEnabled: saved?.promptEnabled ?? false,
      customPrompt: saved?.customPrompt,
    };
  });
  const { azimuth = 0, elevation = 0, scaleIndex = 2, presetId = 'custom', promptEnabled = true, customPrompt } = params;

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onNodeUpdateRef = useRef(onNodeUpdate);
  onNodeUpdateRef.current = onNodeUpdate;

  // 关闭面板时将最后使用的参数持久化到节点 data（再次打开恢复、随撤销回退）
  useEffect(() => {
    return () => {
      onNodeUpdateRef.current(data.id, { multiAngleParams: { ...paramsRef.current } });
    };
  }, [data.id]);

  // 提示词状态策略（v1.6）：调参/拖拽/切自定义都不触碰提示词；
  // 仅 预设点击（填入内置文本）、手动开关、文本框编辑、重置按钮 会改变它
  const updateParam = useCallback((patch: Partial<MultiAngleParams>) => {
    setParams((prev) => ({
      ...prev,
      ...patch,
      presetId: 'custom',
      ...(patch.presetId !== undefined ? { presetId: patch.presetId } : null),
    }));
  }, []);

  const handlePresetClick = useCallback((p: AnglePreset) => {
    setParams((prev) => ({
      ...prev,
      presetId: p.id,
      azimuth: p.azimuth,
      elevation: p.elevation,
      scaleIndex: p.scaleIndex,
      // 仅鱼眼/倾斜预设自动开启提示词并填入指定文本，其余预设关闭并清空（不自动使用提示词）
      promptEnabled: !!p.promptText,
      customPrompt: p.promptText,
    }));
  }, []);

  const handleCustomClick = useCallback(() => {
    setParams((prev) => ({ ...prev, presetId: 'custom' }));
  }, []);

  const handleViewportAzimuth = useCallback((delta: number) => {
    setParams((prev) => ({ ...prev, presetId: 'custom', azimuth: clampAzimuth((prev.azimuth ?? 0) + delta) }));
  }, []);

  const handleViewportElevation = useCallback((delta: number) => {
    setParams((prev) => ({ ...prev, presetId: 'custom', elevation: clampElevation((prev.elevation ?? 0) + delta) }));
  }, []);

  const handleViewportScale = useCallback((index: number) => {
    setParams((prev) => ({ ...prev, presetId: 'custom', scaleIndex: snapScaleIndex(index) }));
  }, []);

  const handleReset = useCallback(() => {
    setParams({ ...DEFAULT_MULTI_ANGLE_PARAMS });
  }, []);

  // v1.12 直传：开关开启 = 文本框内容原样发送；关闭 = 不发送提示词（机位完全由结构化参数驱动）
  const promptText = promptEnabled ? (customPrompt ?? '') : '';

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setParams((prev) => ({ ...prev, customPrompt: value }));
  }, []);

  const handleTogglePrompt = useCallback((v: boolean) => {
    setParams((prev) => ({ ...prev, promptEnabled: v }));
  }, []);

  const handleGenerate = useCallback(() => {
    if (!imageUrl || isWorking) return;
    const prompt = buildAnglePrompt(customPrompt, promptEnabled);
    onClose();
    onGenerateMultiAngle?.(data.id, {
      prompt,
      imageUrl,
      model: GENERATE_MODEL_ID,
      aspectRatio: data.data.aspectRatio || undefined,
      multiAngleParams: { presetId, azimuth, elevation, scaleIndex, promptEnabled, customPrompt },
    });
  }, [imageUrl, isWorking, presetId, azimuth, elevation, scaleIndex, customPrompt, promptEnabled, onClose, onGenerateMultiAngle, data.id, data.data.aspectRatio]);

  return (
    <InverseScaleBox
      fixedContent
      fitViewport
      className="absolute left-1/2 z-30"
      style={{
        top: cardHeight + 8,
        transform: 'translateX(-50%)',
        transformOrigin: 'center top',
      }}
      scaleOrigin="center top"
    >
      {/* 流式化（P0）：尺寸加视口上限——宽窗（≥928px）下与原 896×504 完全一致；
          窄窗时收缩到视口内（四周边距 32px），杜绝溢出裁切。100vw/100vh 为视口单位，
          不受 InverseScaleBox 祖先 transform 影响，无需 JS 测量 */}
      <div
        className="flex flex-col bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg overflow-hidden nodrag nowheel"
        style={{ width: 'min(896px, 100vw - 32px)', height: 'min(504px, 100vh - 32px)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* 标题行 */}
        <div className="flex items-center justify-between px-4 h-10 border-b border-white/10 shrink-0">
          <span className="text-sm font-medium text-white">多角度编辑器</span>
          <button
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* 视角预设 tab */}
        <div className="flex items-center gap-1 px-3 h-10 shrink-0 overflow-x-auto">
          <PresetTab label="自定义" active={presetId === 'custom'} onClick={handleCustomClick} />
          {ANGLE_PRESETS.map((p) => (
            <PresetTab key={p.id} label={p.label} active={presetId === p.id} onClick={() => handlePresetClick(p)} />
          ))}
        </div>

        {/* 主体：左 3D 视口 + 右参数区（流式化 P1：宽容器下分配与原固定 576 一致；
            窄容器时右侧参数区（flex-1 + overflow-y-auto）先收、左视口最后收且不小于
            360 保底，3D 预览内部 ResizeObserver 随宽自动重渲染） */}
        <div className="flex flex-1 min-h-0 gap-3 px-4 pb-2">
          <div className="h-full" style={{ flex: '0 1 576px', minWidth: 360 }}>
            <MultiAngleViewport
              imageUrl={imageUrl}
              azimuth={azimuth}
              elevation={elevation}
              scaleIndex={scaleIndex}
              onAzimuthChange={handleViewportAzimuth}
              onElevationChange={handleViewportElevation}
              onScaleIndexChange={handleViewportScale}
            />
          </div>

          {/* 水平内边距给滑块圆点留出两端各 6px 的伸出空间（滚动容器会裁切越界内容） */}
          <div className="flex-1 min-w-0 flex flex-col gap-2 overflow-y-auto px-2">
            {/* 角度参数 */}
            <div className="flex flex-col gap-2">
            {/* 水平环绕 */}
            <div className="flex flex-col gap-1">
              <SectionLabel text="水平环绕" valueText={`${azimuth}°`} />
              <Slider
                value={azimuth}
                min={0}
                max={359}
                step={1}
                onChange={(v) => updateParam({ azimuth: v })}
                accentColor={ACCENT}
              />
              <div className="grid grid-cols-4 gap-1.5">
                {AZIMUTH_BUTTONS.map((b) => (
                  <MiniButton key={b.label} label={b.label} active={azimuth === b.value} onClick={() => updateParam({ azimuth: b.value })} />
                ))}
              </div>
            </div>

            {/* 垂直俯仰 */}
            <div className="flex flex-col gap-1">
              <SectionLabel text="垂直俯仰" valueText={`${elevation}°`} />
              <Slider
                value={elevation}
                min={-30}
                max={60}
                step={1}
                onChange={(v) => updateParam({ elevation: v })}
                accentColor={ACCENT}
              />
              <div className="grid grid-cols-4 gap-1.5">
                {ELEVATION_BUTTONS.map((b) => (
                  <MiniButton key={b.label} label={b.label} active={elevation === b.value} onClick={() => updateParam({ elevation: b.value })} />
                ))}
              </div>
            </div>

            {/* 景别缩放 */}
            <div className="flex flex-col gap-1">
              <SectionLabel text="景别缩放" valueText={SCALE_LABELS[scaleIndex] ?? ''} />
              <Slider
                value={scaleIndex}
                min={0}
                max={4}
                step={2}
                onChange={(v) => updateParam({ scaleIndex: snapScaleIndex(v) })}
                accentColor={ACCENT}
                valueFormatter={(v) => SCALE_LABELS[clampScaleIndex(v)] ?? String(v)}
              />
              <div className="grid grid-cols-3 gap-1.5">
                {SCALE_BUTTONS.map((b) => (
                  <MiniButton key={b.label} label={b.label} active={scaleIndex === b.value} onClick={() => updateParam({ scaleIndex: b.value })} />
                ))}
              </div>
            </div>
            </div>

            {/* 提示词 */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-white/80">提示词</span>
                <ToggleSwitch checked={promptEnabled} onChange={handleTogglePrompt} />
              </div>
              <textarea
                value={promptText}
                onChange={handlePromptChange}
                disabled={!promptEnabled}
                placeholder="输入提示词，开启后将随生成直接发送"
                className="w-full h-12 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] leading-4 text-white/90 outline-none resize-none focus:border-white/30 placeholder:text-white/25 disabled:opacity-40"
              />
            </div>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="flex items-center justify-between px-4 h-12 border-t border-white/10 shrink-0">
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleReset}
            className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] text-white/60 hover:text-white hover:bg-[#3a3a3d] transition-colors cursor-pointer"
          >
            <RotateCcw size={12} />
            重置参数
          </button>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5" aria-hidden />
            {/* 积分消耗显示（与节点底部面板一致，单价 = 固定模型定价，未配置 = 0） */}
            <span
              className="flex items-center gap-1 text-[12px] text-white/40"
              title="本次生成预计消耗积分"
            >
              <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} draggable={false} />
              {/* 预留三位数积分宽度（tabular-nums 等宽数字），位数变化不引起布局跳动 */}
              <span className="min-w-[22px] tabular-nums text-left">{getModelCost(GENERATE_MODEL_ID, models)}</span>
            </span>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleGenerate}
              disabled={!imageUrl || isWorking}
              title={!imageUrl ? '请先生成或上传图片' : isWorking ? '生成中，请稍候' : '生成多角度图片'}
              className="flex items-center justify-center w-8 h-8 bg-white rounded-[8px] text-[#1c1c1e] transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
            >
              {/* v1.4.11：图标统一为项目 send-arrow.svg（与其他节点底部面板按钮同字形） */}
              <img src="/icons/send-arrow.svg" alt="生成" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
            </button>
          </div>
        </div>
      </div>
    </InverseScaleBox>
  );
};
